/**
 * Incremental parser for `text/event-stream` bodies (the WHATWG
 * "event stream interpretation"), fed with decoded text chunks as they
 * arrive. Chunks can end anywhere, including inside a line or between the
 * CR and LF of a line break.
 */

export interface SseMessage {
  /** The `id` field, or the last id seen before it (empty if none). */
  id: string;
  /** The `event` field; `message` when the frame has none. */
  event: string;
  data: string;
}

export interface SseHandlers {
  onMessage: (message: SseMessage) => void;
  /** A `retry: <ms>` hint. */
  onRetry?: (ms: number) => void;
  /** A comment line (`: heartbeat`), without the colon. */
  onComment?: (text: string) => void;
}

export class SseParser {
  private buffer = '';
  /** The previous chunk ended with CR: a following LF belongs to it. */
  private pendingCr = false;
  private data: string[] = [];
  private eventType = '';
  private lastEventId = '';
  private hasData = false;

  constructor(private readonly handlers: SseHandlers) {}

  /** The last id received, to send as Last-Event-ID when reconnecting. */
  get lastId(): string {
    return this.lastEventId;
  }

  feed(chunk: string) {
    let text = chunk;
    if (this.pendingCr && text.startsWith('\n')) text = text.slice(1);
    this.pendingCr = false;
    this.buffer += text;

    let start = 0;
    for (let i = 0; i < this.buffer.length; i++) {
      const char = this.buffer[i];
      if (char !== '\n' && char !== '\r') continue;
      this.line(this.buffer.slice(start, i));
      if (char === '\r') {
        if (i + 1 < this.buffer.length) {
          if (this.buffer[i + 1] === '\n') i++;
        } else {
          this.pendingCr = true;
        }
      }
      start = i + 1;
    }
    this.buffer = this.buffer.slice(start);
  }

  /** The stream ended: an unterminated frame is discarded, as the spec says. */
  end() {
    this.buffer = '';
    this.pendingCr = false;
    this.resetFrame();
  }

  private resetFrame() {
    this.data = [];
    this.eventType = '';
    this.hasData = false;
  }

  private line(line: string) {
    if (line === '') {
      this.dispatch();
      return;
    }
    if (line.startsWith(':')) {
      this.handlers.onComment?.(line.slice(1).trimStart());
      return;
    }
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    switch (field) {
      case 'data':
        this.data.push(value);
        this.hasData = true;
        break;
      case 'event':
        this.eventType = value;
        break;
      case 'id':
        if (!value.includes('\0')) this.lastEventId = value;
        break;
      case 'retry':
        if (/^\d+$/.test(value)) this.handlers.onRetry?.(Number(value));
        break;
      default:
        // Unknown fields are ignored.
        break;
    }
  }

  private dispatch() {
    if (!this.hasData) {
      this.resetFrame();
      return;
    }
    const message: SseMessage = {
      id: this.lastEventId,
      event: this.eventType || 'message',
      data: this.data.join('\n'),
    };
    this.resetFrame();
    this.handlers.onMessage(message);
  }
}
